'use client';

/**
 * /tags — editorial directory of every tag in the gallery.
 *
 * Layout (top → bottom):
 *   1. Hero: serif title, summary stats, filter input
 *   2. Top tags: 6 oversized "feature" cards with hue tints derived from
 *      the tag name's hash (so the same tag always gets the same color
 *      across reloads), prompt count badge, and a soft brand glow on hover
 *   3. All tags: A–Z grouped sections with bullet-style chips. Counts shown
 *      inline with monospace numerals so columns line up
 *   4. Sort toggle (Featured / Newest / A–Z) — drives the order of #3
 *
 * Design choices:
 *   - Each tag gets a deterministic hue from `hashHue(name)`. Cards tint with
 *     that hue both for chip background AND a soft radial glow on hover.
 *     This avoids the previous "wall of identical pills" feel.
 *   - Top tags sit above the fold and use a larger card so users land on
 *     "what's worth browsing" before being overwhelmed by 200+ pills.
 *   - Empty filter state has a friendly message + CTA back to all tags.
 */
import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';

type Tag = { name: string; count: number };

/**
 * Cheap deterministic string hash → hue (0–360). Same input always returns
 * the same hue, so a tag's color is stable across renders and sessions.
 */
function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

type SortMode = 'featured' | 'alpha' | 'count';

export default function TagsPage() {
  const router = useRouter();
  const t = useT();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortMode>('featured');

  useEffect(() => {
    api.tags.list()
      .then(setTags)
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }, []);

  // Featured = top 6 by count. We always derive these from the FULL list
  // (not the filtered list) so users still see the top tags while typing
  // a filter that doesn't include them.
  const featured = useMemo(() => {
    return [...tags].sort((a, b) => b.count - a.count).slice(0, 6);
  }, [tags]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = s ? tags.filter((t) => t.name.toLowerCase().includes(s)) : tags;
    list = [...list];
    if (sort === 'alpha') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === 'count') {
      list.sort((a, b) => b.count - a.count);
    } else {
      // Featured: count first, then alpha as tiebreak. Matches what most
      // editorial-style "all tags" pages do.
      list.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    }
    return list;
  }, [tags, search, sort]);

  // Alphabetical groups for the alpha sort. Ignored for the other sorts —
  // we render a flat list there.
  const alphaGroups = useMemo(() => {
    if (sort !== 'alpha') return null;
    const groups: Record<string, Tag[]> = {};
    for (const t of filtered) {
      const first = t.name[0]?.toUpperCase();
      const key = !first ? '#' : /[A-Z]/.test(first) ? first : '#';
      (groups[key] = groups[key] || []).push(t);
    }
    return Object.keys(groups).sort().map((k) => ({ letter: k, items: groups[k] }));
  }, [filtered, sort]);

  const totalPrompts = useMemo(() => {
    // Each prompt can have multiple tags so this isn't a direct sum, but
    // approximate it for the hero subtitle. Underestimates but reads naturally.
    return tags.reduce((m, t) => Math.max(m, t.count), 0);
  }, [tags]);

  return (
    <>
      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="page-pad" style={{
        paddingTop: 40, paddingBottom: 28, maxWidth: 1100,
      }}>
        <button
          onClick={() => router.back()}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: 'var(--ink-3)',
            background: 'transparent', border: 0, cursor: 'pointer',
            padding: 0, marginBottom: 18,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
          {t('tags.back_to_gallery')}
        </button>

        <div style={{
          fontSize: 11, textTransform: 'uppercase',
          letterSpacing: '0.16em', fontWeight: 600,
          color: 'var(--accent)', marginBottom: 12,
        }}>
          {t('tags.eyebrow')}
        </div>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontVariationSettings: '"opsz" 110, "SOFT" 50',
          fontSize: 'clamp(40px, 5.4vw, 68px)',
          fontWeight: 400,
          letterSpacing: '-0.035em',
          lineHeight: 1.02,
          margin: '0 0 18px',
          color: 'var(--ink)',
          textWrap: 'balance',
        }}>
          {t('tags.title_before')}{' '}
          <em style={{
            fontStyle: 'italic', color: 'var(--accent)', fontWeight: 400,
          }}>
            {t('tags.title_em')}
          </em>
          {t('tags.title_after')}
        </h1>
        <p style={{
          fontSize: 16, lineHeight: 1.55, color: 'var(--ink-2)',
          maxWidth: 600, margin: '0 0 26px',
        }}>
          {loading ? (
            t('tags.loading_directory')
          ) : (
            <>
              <b style={{ color: 'var(--ink)' }}>{tags.length.toLocaleString()}</b> {t('tags.summary_tags_label')}
              {totalPrompts > 0 && (
                <> · {t('tags.summary_top_tag')} <b style={{ color: 'var(--ink)' }}>{totalPrompts}</b> {t('tags.summary_prompts_label')}</>
              )}.
              {' '}{t('tags.summary_tail')}
            </>
          )}
        </p>

        {/* Filter input — wider + serif placeholder for editorial feel */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px',
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-full)',
          maxWidth: 480,
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.boxShadow = '0 0 0 4px color-mix(in oklch, var(--accent) 12%, transparent)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--line)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--ink-3)' }}>
            search
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={loading ? t('tags.search_placeholder_loading') : t('tags.search_placeholder', { count: tags.length })}
            style={{
              flex: 1, fontSize: 14,
              background: 'transparent', border: 0, outline: 'none',
              color: 'var(--ink)',
              fontFamily: 'var(--font-sans)',
            }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              style={{
                background: 'transparent', border: 0, cursor: 'pointer',
                color: 'var(--ink-3)',
                display: 'inline-flex', alignItems: 'center',
              }}
              aria-label="Clear filter"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
            </button>
          )}
        </div>
      </section>

      {/* ── Featured (only when no filter active and not searching) ─ */}
      {!search.trim() && !loading && featured.length > 0 && (
        <section className="page-pad" style={{ paddingBottom: 36, maxWidth: 1400 }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 14,
            marginBottom: 18, flexWrap: 'wrap',
          }}>
            <span style={{
              fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em',
              color: 'var(--ink-3)', fontWeight: 600,
            }}>
              Editor&rsquo;s pick
            </span>
            <h2 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 22, fontWeight: 400,
              letterSpacing: '-0.02em',
              margin: 0, color: 'var(--ink)',
            }}>
              Most used
            </h2>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 14,
          }}>
            {featured.map((t, i) => (
              <FeaturedTagCard key={t.name} tag={t} rank={i + 1} />
            ))}
          </div>
        </section>
      )}

      {/* ── All tags ─────────────────────────────────────────────── */}
      <section className="page-pad" style={{ paddingBottom: 80, maxWidth: 1400 }}>
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 14,
          marginBottom: 18, flexWrap: 'wrap',
        }}>
          <span style={{
            fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em',
            color: 'var(--ink-3)', fontWeight: 600,
          }}>
            Directory
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 22, fontWeight: 400,
            letterSpacing: '-0.02em',
            margin: 0, color: 'var(--ink)',
          }}>
            {search.trim() ? `Matching "${search.trim()}"` : 'All tags'}
          </h2>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {filtered.length} {filtered.length === 1 ? 'tag' : 'tags'}
          </span>
          <div className="seg" role="tablist" aria-label="Sort" style={{ marginLeft: 'auto' }}>
            {([
              { v: 'featured' as const, label: 'Featured' },
              { v: 'count' as const, label: 'By count' },
              { v: 'alpha' as const, label: 'A–Z' },
            ]).map((s) => (
              <button
                key={s.v}
                aria-pressed={sort === s.v}
                onClick={() => setSort(s.v)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Skeleton during load */}
        {loading ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} className="animate-pulse">
            {Array.from({ length: 26 }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: 32,
                  background: 'var(--bg-soft)',
                  borderRadius: 'var(--radius-full)',
                  width: `${70 + ((i * 41) % 110)}px`,
                }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="ps-empty">
            <h3>No tags match &ldquo;{search}&rdquo;</h3>
            <p>Try a different keyword, or clear the filter to see all {tags.length} tags.</p>
            <button
              onClick={() => setSearch('')}
              className="ps-btn ps-btn-secondary"
              style={{ marginTop: 14 }}
            >
              Clear filter
            </button>
          </div>
        ) : alphaGroups ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            {alphaGroups.map(({ letter, items }) => (
              <div key={letter}>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 12,
                  marginBottom: 10,
                  borderBottom: '1px solid var(--line-soft)',
                  paddingBottom: 8,
                }}>
                  <h3 style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: 22, fontWeight: 500,
                    letterSpacing: '-0.01em',
                    color: 'var(--accent)',
                    margin: 0,
                  }}>
                    {letter}
                  </h3>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {items.length}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {items.map((t) => <TagPill key={t.name} tag={t} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Featured / by-count sort: flat wall of pills, sized by frequency
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {filtered.map((t) => <TagPill key={t.name} tag={t} sized />)}
          </div>
        )}
      </section>
    </>
  );
}

/** Top-tag oversized card — used in the featured strip. */
function FeaturedTagCard({ tag, rank }: { tag: Tag; rank: number }) {
  const hue = hashHue(tag.name);
  return (
    <Link
      href={`/?tags=${encodeURIComponent(tag.name)}`}
      className="no-underline"
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        gap: 8,
        padding: '20px 18px 18px',
        background: 'var(--bg-raised)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 22px -8px oklch(0.7 0.16 ${hue} / 0.5)`;
        e.currentTarget.style.borderColor = `oklch(0.7 0.16 ${hue} / 0.55)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.borderColor = 'var(--line)';
      }}
    >
      {/* Hue-tinted glow blob */}
      <div style={{
        position: 'absolute', top: -30, right: -30,
        width: 160, height: 160,
        background: `radial-gradient(circle, oklch(0.78 0.13 ${hue} / 0.45), transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--ink-3)',
          letterSpacing: '0.05em',
          fontWeight: 500,
        }}>
          #{rank.toString().padStart(2, '0')}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          padding: '2px 8px',
          background: `oklch(0.95 0.04 ${hue})`,
          color: `oklch(0.4 0.14 ${hue})`,
          borderRadius: 'var(--radius-full)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {tag.count} prompt{tag.count === 1 ? '' : 's'}
        </span>
      </div>

      <div style={{
        position: 'relative',
        fontFamily: 'var(--font-serif)',
        fontSize: 28,
        fontWeight: 500,
        letterSpacing: '-0.025em',
        color: 'var(--ink)',
        lineHeight: 1.1,
        marginTop: 2,
      }}>
        <span style={{
          color: `oklch(0.55 0.16 ${hue})`,
          fontStyle: 'italic',
          fontWeight: 400,
          marginRight: 4,
        }}>
          #
        </span>
        {tag.name}
      </div>
    </Link>
  );
}

/** Inline tag pill used in the All-tags directory. */
function TagPill({ tag, sized = false }: { tag: Tag; sized?: boolean }) {
  const hue = hashHue(tag.name);
  // When `sized`, scale the chip with a log-curve on count so popular tags
  // visually stand out without dominating the layout.
  const fontSize = sized
    ? Math.min(20, Math.max(12, 12 + Math.log2(tag.count + 1) * 1.3))
    : 13;
  const padX = sized ? Math.max(11, Math.min(18, fontSize - 1)) : 12;
  return (
    <Link
      href={`/?tags=${encodeURIComponent(tag.name)}`}
      className="no-underline"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: `${sized ? 5 : 4}px ${padX}px`,
        fontSize,
        fontWeight: sized ? 500 : 500,
        background: 'var(--bg-raised)',
        color: 'var(--ink)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-full)',
        cursor: 'pointer',
        transition: 'all 0.12s ease',
        lineHeight: 1.3,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `oklch(0.7 0.16 ${hue} / 0.6)`;
        e.currentTarget.style.background = `oklch(0.97 0.03 ${hue})`;
        e.currentTarget.style.color = `oklch(0.35 0.16 ${hue})`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--line)';
        e.currentTarget.style.background = 'var(--bg-raised)';
        e.currentTarget.style.color = 'var(--ink)';
      }}
    >
      <span style={{ color: `oklch(0.55 0.16 ${hue})`, fontStyle: 'italic' }}>#</span>
      {tag.name}
      <span style={{
        fontSize: Math.max(10, fontSize - 3),
        color: 'var(--ink-3)',
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        marginLeft: 2,
      }}>
        {tag.count}
      </span>
    </Link>
  );
}
